import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import PulseDot from './PulseDot';
import Icon from './Icon';

const TONE = { active: 'ok', silent: 'crit', unknown: 'idle' };

export default function ServerSwitcher() {
  const { t } = useTranslation();
  const { servers, activeServer, activeServerKey, selectServer, serverSlots } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  if (!servers || servers.length === 0) return null;

  const labelFor = (s, i) => s.label || t('servers.default_name', { n: (s.order ?? i) + 1 });
  const current = activeServer || servers[0];

  return (
    <div className="srv-switch" ref={ref}>
      <button className="srv-switch-btn" onClick={() => setOpen((v) => !v)}>
        <PulseDot tone={TONE[current?.agentStatus] || 'idle'} animated={current?.agentStatus === 'active'} />
        <span className="srv-switch-label">{labelFor(current, 0)}</span>
        {servers.length > 1 && <Icon name="arrow" size={12} style={{ transform: open ? 'rotate(-90deg)' : 'rotate(90deg)', opacity: 0.5 }} />}
      </button>

      {open && (
        <div className="srv-switch-menu">
          {servers.map((s, i) => (
            <button
              key={s.key}
              className={`srv-switch-item ${s.key === activeServerKey ? 'active' : ''}`}
              onClick={() => { selectServer(s.key); setOpen(false); }}
            >
              <PulseDot tone={s.status !== 'active' ? 'idle' : TONE[s.agentStatus] || 'idle'} animated={false} />
              <span>{labelFor(s, i)}</span>
              {s.status !== 'active' && <span className="srv-tag">{t('servers.suspended')}</span>}
              {s.needsSetup && s.status === 'active' && <span className="srv-tag warn">{t('servers.needs_setup')}</span>}
              {s.health && <span className={`srv-health ${s.health.label}`}>{s.health.score}</span>}
            </button>
          ))}
          <div className="srv-switch-foot">
            <button onClick={() => { setOpen(false); navigate('/dashboard/servers'); }}>
              <Icon name="grid" size={13} /> {t('servers.manage')}
            </button>
            <button onClick={() => { setOpen(false); navigate('/checkout?add=1'); }}>
              <Icon name="plus" size={13} /> {t('servers.add')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
